import type { AssetTypeStatus, Stored } from "@overmap-ai/common"
import type { ReactNode } from "react"

import {
	AssetTypeStatusEqualsFilterModifier,
	AssetTypeStatusExcludesFilterModifier,
	AssetTypeStatusIncludesFilterModifier,
	AssetTypeStatusNotEqualsFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { AssetTypeStatusFilterCell } from "./AssetTypeStatusFilterCell"

type AssetTypeStatusFieldValue = AssetTypeStatus["uuid"]
type AssetTypeStatusFilterValue = AssetTypeStatus["uuid"][] | AssetTypeStatus["uuid"]

export class AssetTypeStatusFilterDef extends BaseFilterDef<AssetTypeStatusFieldValue, AssetTypeStatusFilterValue> {
	readonly id = "asset-type-status"
	readonly defaultFilterValue: AssetTypeStatusFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"

	readonly assetTypeStatuses: Stored<AssetTypeStatus>[]

	constructor(assetTypeStatuses: Stored<AssetTypeStatus>[]) {
		super()
		this.assetTypeStatuses = assetTypeStatuses
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: AssetTypeStatusEqualsFilterModifier,
			isValueValid: (_value: AssetTypeStatusFieldValue): _value is AssetTypeStatus["uuid"] => true,
			isFilterValueValid: (filterValue: AssetTypeStatusFilterValue): filterValue is AssetTypeStatus["uuid"] =>
				!Array.isArray(filterValue),
		}),
		notEquals: createFilterModifierConfig({
			modifier: AssetTypeStatusNotEqualsFilterModifier,
			isValueValid: (_value: AssetTypeStatusFieldValue): _value is AssetTypeStatus["uuid"] => true,
			isFilterValueValid: (filterValue: AssetTypeStatusFilterValue): filterValue is AssetTypeStatus["uuid"] =>
				!Array.isArray(filterValue),
		}),
		includes: createFilterModifierConfig({
			modifier: AssetTypeStatusIncludesFilterModifier,
			isValueValid: (_value: AssetTypeStatusFieldValue): _value is AssetTypeStatus["uuid"] => true,
			isFilterValueValid: (filterValue: AssetTypeStatusFilterValue): filterValue is AssetTypeStatus["uuid"][] =>
				Array.isArray(filterValue),
		}),
		excludes: createFilterModifierConfig({
			modifier: AssetTypeStatusExcludesFilterModifier,
			isValueValid: (_value: AssetTypeStatusFieldValue): _value is AssetTypeStatus["uuid"] => true,
			isFilterValueValid: (filterValue: AssetTypeStatusFilterValue): filterValue is AssetTypeStatus["uuid"][] =>
				Array.isArray(filterValue),
		}),
	}

	render(props: FilterDefRenderProps<AssetTypeStatusFilterDef>): ReactNode {
		return <AssetTypeStatusFilterCell key={props.filter.id} {...props} />
	}
}
